import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  MenuItem,
} from "@mui/material";
import { NotificationManager } from "react-notifications";
import { useNavigate, useParams } from "react-router-dom";
import CommonLoader from "../../common/CommonLoader";
import {
  SaveEmailTemplate,
  UpdateEmailTemplate,
  GetEmailTemplateById,
} from "../../../services/EmailTemplatesService";
import ViewEmailTemplate from "./ViewEmailTemplate";
const AddEmailTemplate = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    type: "",
    bodyHtml: "",
    fromAddress: "",
    subjectLine: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Email template types aligned with backend EmailType enum
const templateTypes = [
  { value: 0, label: "Not Specified" },
  { value: 1, label: "Drinking Water" },
  { value: 2, label: "Drinking Water (Non-Regulated)" },
  { value: 3, label: "Waste Water" },
  { value: 4, label: "Indoor Pool" },
  { value: 5, label: "Outdoor Pool" },
  { value: 6, label: "Multiple" },
  { value: 7, label: "ETK" },
  { value: 8, label: "ETK Distributor" },
  { value: 9, label: "ETK Wholesale" },
  { value: 10, label: "Bathing Beach" },
  { value: 11, label: "Bottle Order Sent" },
  { value: 12, label: "ETK Received" },
  { value: 13, label: "Invoice Sent" },
  { value: 14, label: "Sample Receipt" },
  { value: 15, label: "Quote" },
  { value: 16, label: "Rating" }
];


  useEffect(() => {
    if (id) {
      fetchTemplateData(parseInt(id, 10));
    }
  }, [id]);

  const fetchTemplateData = async (templateId) => {
    setLoading(true);
    try {
      const response = await GetEmailTemplateById(templateId);
      if (response && response.results) {
        setFormData(response.results);
      } else {
        NotificationManager.error("Failed to fetch template data");
      }
    } catch (error) {
      NotificationManager.error("Error fetching template data");
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.type) newErrors.type = "Template type is required";
    if (!formData.subjectLine) newErrors.subjectLine = "Subject line is required";
    if (!formData.fromAddress) newErrors.fromAddress = "From address is required";
    if (!formData.bodyHtml) newErrors.bodyHtml = "Email body is required";

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.fromAddress && !emailRegex.test(formData.fromAddress)) {
      newErrors.fromAddress = "Invalid email address";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const templateData = {
      ...formData,
      type: parseInt(formData.type, 10),
      id: id ? parseInt(id, 10) : undefined,
    };

    setLoading(true);
    try {
      if (id) {
        await UpdateEmailTemplate(templateData);
        NotificationManager.success("Template updated successfully");
      } else {
        await SaveEmailTemplate(templateData);
        NotificationManager.success("Template added successfully");
      }
      navigate("/email-templates");
    } catch (error) {
      NotificationManager.error(error.message || "Error saving template");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCancel = () => {
    navigate("/email-templates");
  };

  return (
    <>
      <Paper elevation={3} sx={{ padding: 3, margin: "auto" }}>
        <Typography variant="h5" gutterBottom>
          {id ? "Edit Email Template" : "Add New Email Template"}
        </Typography>
        <Grid container spacing={2}>
          {/* Template Type */}
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Template Type"
              value={formData.type}
              onChange={handleChange("type")}
              size="small"
              fullWidth
              required
              error={!!errors.type}
              helperText={errors.type}
            >
              {templateTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* From Address */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="From Address"
              type="email"
              value={formData.fromAddress || ""}
              onChange={handleChange("fromAddress")}
              size="small"
              fullWidth
              required
              error={!!errors.fromAddress}
              helperText={errors.fromAddress}
              placeholder="noreply@example.com"
            />
          </Grid>

          {/* Subject Line */}
          <Grid item xs={12}>
            <TextField
              label="Subject Line"
              value={formData.subjectLine || ""}
              onChange={handleChange("subjectLine")}
              size="small"
              fullWidth
              required
              error={!!errors.subjectLine}
              helperText={errors.subjectLine}
            />
          </Grid>

          {/* Body HTML */}
          <Grid item xs={12}>
            <TextField
              label="Email Body (HTML)"
              value={formData.bodyHtml || ""}
              onChange={handleChange("bodyHtml")}
              size="small"
              fullWidth
              required
              multiline
              rows={10}
              error={!!errors.bodyHtml}
              helperText={errors.bodyHtml}
              placeholder="Enter HTML content for the email body..."
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12} container justifyContent="flex-end" spacing={2}>
            <Grid item>
              <Button
                onClick={handleCancel}
                color="secondary"
                variant="outlined"
              >
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                onClick={handleSave}
                color="primary"
                variant="contained"
              >
                {id ? "Update" : "Save"}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      
      <ViewEmailTemplate template={formData} />
      <CommonLoader loading={loading} />
    </>
  );
};

export default AddEmailTemplate;